import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { Gift, Users, Coins, Copy, Check, Share2, TrendingUp, Info } from "lucide-react";

export default function Referral() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!user || user === false) return;
    api
      .get("/referral/me")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat data referal"))
      .finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const code = data?.referral_code || "";
  const link = `${window.location.origin}/register?ref=${code}`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Disalin ke clipboard");
      setTimeout(() => setCopied(""), 1800);
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const share = async () => {
    const text = `Yuk belanja & ikut lelang di Ustore! Pakai kode referalku "${code}" saat daftar: ${link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Ustore Referral", text, url: link });
      } catch {}
    } else {
      copy(text, "share");
    }
  };

  const cfg = data?.config || {};

  return (
    <div data-testid="referral-page" className="max-w-5xl mx-auto px-4 md:px-8 py-12">
      <span className="tag tag-accent">// AJAK TEMAN</span>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3 mb-2 flex items-center gap-3">
        <Gift className="w-8 h-8 text-[#00F0FF]" /> Program Referal
      </h1>
      <p className="text-white/55 mb-8">
        Bagikan kode referalmu. Saat temanmu daftar & belanja, kalian berdua dapat bonus saldo dengan nominal yang sama.
      </p>

      {busy ? (
        <div className="text-white/50 text-center py-24">Memuat data referal...</div>
      ) : data?.enabled === false ? (
        <div className="border border-[#FFD700]/40 bg-[#FFD700]/5 p-6 flex items-center gap-3" data-testid="referral-disabled">
          <Info className="w-5 h-5 text-[#FFD700] shrink-0" />
          <p className="text-white/70">Program referal sedang tidak aktif. Cek kembali nanti ya!</p>
        </div>
      ) : (
        <>
          {/* Kode & Link */}
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-4 mb-8">
            <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-6" data-testid="referral-code-card">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Kode Referalmu</div>
              <div className="flex items-center gap-3">
                <span className="font-display font-black text-3xl md:text-4xl text-[#00F0FF] tracking-widest" data-testid="referral-code">
                  {code}
                </span>
                <button
                  onClick={() => copy(code, "code")}
                  data-testid="copy-code-btn"
                  className="p-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] transition-colors"
                  title="Salin kode"
                >
                  {copied === "code" ? <Check className="w-4 h-4 text-[#00FF66]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Link Undangan</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={link}
                  data-testid="referral-link"
                  className="flex-1 bg-black border border-white/15 text-white/80 text-sm px-3 py-2.5 truncate"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={() => copy(link, "link")}
                  data-testid="copy-link-btn"
                  className="p-2.5 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] transition-colors shrink-0"
                  title="Salin link"
                >
                  {copied === "link" ? <Check className="w-4 h-4 text-[#00FF66]" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={share}
                  data-testid="share-btn"
                  className="p-2.5 border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors shrink-0"
                  title="Bagikan"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Statistik */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="border border-white/10 p-6" data-testid="stat-referred">
              <Users className="w-5 h-5 text-[#7C6EFF] mb-3" />
              <div className="font-display font-black text-3xl text-white">{data.total_referred || 0}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Teman Terdaftar</div>
            </div>
            <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-6" data-testid="stat-earned">
              <Coins className="w-5 h-5 text-[#00FF66] mb-3" />
              <div className="font-display font-black text-3xl text-[#00FF66]">{formatIDR(data.total_earned || 0)}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Total Bonus Diterima</div>
            </div>
            <div className="border border-white/10 p-6" data-testid="stat-month">
              <TrendingUp className="w-5 h-5 text-[#FFD700] mb-3" />
              <div className="font-display font-black text-3xl text-white">{formatIDR(data.earned_this_month || 0)}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Bonus Bulan Ini</div>
              {data.monthly_cap > 0 && (
                <div className="text-[10px] text-white/40 mt-1">
                  Sisa limit: {formatIDR(data.remaining_this_month || 0)} / {formatIDR(data.monthly_cap)}
                </div>
              )}
            </div>
          </div>

          {/* Cara kerja / tier */}
          <div className="border border-white/10 p-6 mb-8" data-testid="referral-howto">
            <h2 className="font-display font-bold text-xl text-white mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-[#00F0FF]" /> Cara Kerja
            </h2>
            <ol className="space-y-2 text-sm text-white/70 mb-4 list-decimal list-inside">
              <li>Bagikan kode / link referalmu ke teman.</li>
              <li>Teman daftar akun pakai kodemu.</li>
              <li>Saat belanjanya memenuhi syarat, kamu dan temanmu mendapat bonus saldo otomatis dengan nominal yang sama.</li>
            </ol>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Belanja min. {formatIDR(cfg.tier1_min || 0)}</div>
                <div className="font-display font-black text-2xl text-[#00F0FF] mt-1">+{formatIDR(cfg.tier1_bonus || 0)} / orang</div>
              </div>
              <div className="border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Belanja min. {formatIDR(cfg.tier2_min || 0)}</div>
                <div className="font-display font-black text-2xl text-[#00FF66] mt-1">+{formatIDR(cfg.tier2_bonus || 0)} / orang</div>
              </div>
            </div>
          </div>

          {/* Riwayat bonus */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-white/10 p-6 h-fit">
              <h3 className="font-display font-bold text-lg text-white mb-4">Riwayat Bonus</h3>
              {(!data.events || data.events.length === 0) ? (
                <p className="text-white/50 text-sm" data-testid="no-events">Belum ada bonus. Ajak temanmu sekarang!</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="events-list">
                  {data.events.map((e, i) => (
                    <div key={e.id || i} className="flex items-center gap-3 border-b border-white/5 pb-2">
                      <Coins className={`w-4 h-4 shrink-0 ${e.status === "paid" ? "text-[#00FF66]" : "text-white/40"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{e.referee_name || "Temanmu"}</p>
                        <p className="text-[10px] text-white/40">
                          {e.created_at ? new Date(e.created_at).toLocaleString("id-ID") : ""}
                          {e.status === "capped" ? " · limit tercapai" : ""}
                        </p>
                      </div>
                      <p className={`font-bold text-sm ${e.status === "paid" ? "text-[#00FF66]" : "text-white/40"}`}>
                        {e.status === "paid" ? "+" : ""}
                        {formatIDR(e.amount || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-white/10 p-6 h-fit">
              <h3 className="font-display font-bold text-lg text-white mb-4">Teman yang Diajak</h3>
              {(!data.signups || data.signups.length === 0) ? (
                <p className="text-white/50 text-sm" data-testid="no-signups">Belum ada yang mendaftar pakai kodemu.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="signups-list">
                  {data.signups.map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-3 border-b border-white/5 pb-2">
                      <Users className="w-4 h-4 shrink-0 text-[#7C6EFF]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{s.referee_name || s.referee_email || "Anonim"}</p>
                        <p className="text-[10px] text-white/40">
                          {s.created_at ? new Date(s.created_at).toLocaleString("id-ID") : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
